/**
 * VendorSyncManager - Component for managing vendor catalog syncs
 * Phase 4: Sync Engine UI
 */
import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Progress } from '../../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  RefreshCw,
  Upload,
  Play,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  FileSpreadsheet,
  Trash2,
  History,
  TrendingUp,
  TrendingDown,
  Minus
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status colors
const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-yellow-500', icon: Clock },
  running: { label: 'Running', color: 'bg-blue-500', icon: RefreshCw },
  completed: { label: 'Completed', color: 'bg-green-500', icon: CheckCircle },
  failed: { label: 'Failed', color: 'bg-red-500', icon: XCircle },
  partial: { label: 'Partial', color: 'bg-amber-500', icon: AlertTriangle },
  cancelled: { label: 'Cancelled', color: 'bg-gray-500', icon: XCircle }
};

const VendorSyncManager = ({ vendorId, vendorName, vendorCode, onSyncComplete }) => {
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncJobs, setSyncJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadOptions, setUploadOptions] = useState({
    update_existing: true,
    skip_unchanged: true
  });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Polling interval for running jobs
  const pollIntervalRef = useRef(null);

  useEffect(() => {
    if (vendorId) {
      loadSyncStatus();
      loadSyncJobs();
    }
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [vendorId]);

  // Poll for updates when a job is running
  useEffect(() => {
    const hasRunningJob = syncJobs.some(j => j.status === 'pending' || j.status === 'running');
    
    if (hasRunningJob && !pollIntervalRef.current) {
      pollIntervalRef.current = setInterval(() => {
        loadSyncStatus();
        loadSyncJobs();
      }, 2000);
    } else if (!hasRunningJob && pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, [syncJobs]);

  const loadSyncStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/catalog/sync/status/${vendorId}`);
      if (res.ok) {
        setSyncStatus(await res.json());
      }
    } catch (error) {
      console.error('Error loading sync status:', error);
    }
  };

  const loadSyncJobs = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/catalog/sync/jobs?vendor_id=${vendorId}&limit=10`);
      if (res.ok) {
        const data = await res.json();
        setSyncJobs(data.sync_jobs || []);
      }
    } catch (error) {
      console.error('Error loading sync jobs:', error);
    }
    setLoading(false);
  };

  const triggerManualSync = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API_URL}/api/catalog/sync/trigger/${vendorId}`, {
        method: 'POST'
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success('Sync started');
        loadSyncJobs();
        loadSyncStatus();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to start sync');
      }
    } catch (error) {
      toast.error('Error starting sync');
    }
    setSyncing(false);
  };

  const uploadCatalogFile = async () => {
    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('update_existing', uploadOptions.update_existing);
      formData.append('skip_unchanged', uploadOptions.skip_unchanged);

      const res = await fetch(`${API_URL}/api/catalog/sync/upload/${vendorId}`, {
        method: 'POST',
        body: formData
      });

      if (res.ok) {
        toast.success('File uploaded, sync started');
        setShowUploadModal(false);
        setUploadFile(null);
        loadSyncJobs();
        loadSyncStatus();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to upload file');
      }
    } catch (error) {
      toast.error('Error uploading file');
    }
    setUploading(false);
  };

  const cancelJob = async (jobId) => {
    try {
      const res = await fetch(`${API_URL}/api/catalog/sync/jobs/${jobId}/cancel`, {
        method: 'POST'
      });
      if (res.ok) {
        toast.success('Sync cancelled');
        loadSyncJobs();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to cancel');
      }
    } catch (error) {
      toast.error('Error cancelling sync');
    }
  };

  const deleteJob = async (jobId) => {
    try {
      const res = await fetch(`${API_URL}/api/catalog/sync/jobs/${jobId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        toast.success('Job deleted');
        loadSyncJobs();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to delete');
      }
    } catch (error) {
      toast.error('Error deleting job');
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '—';
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString();
  };

  const runningJob = syncJobs.find(j => j.status === 'pending' || j.status === 'running');
  const lastSync = syncStatus?.sync_config?.last_sync_at;

  return (
    <div className="space-y-4">
      {/* Current Status Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center justify-between">
            <span className="flex items-center gap-2">
              <RefreshCw className="w-4 h-4" />
              Sync Status
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowHistoryModal(true)}
                data-testid="sync-history-btn"
              >
                <History className="w-4 h-4 mr-1" />
                History
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowUploadModal(true)}
                data-testid="upload-catalog-btn"
              >
                <Upload className="w-4 h-4 mr-1" />
                Upload File
              </Button>
              <Button
                size="sm"
                onClick={triggerManualSync}
                disabled={syncing || !!runningJob}
                data-testid="trigger-sync-btn"
              >
                {syncing || runningJob ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Play className="w-4 h-4 mr-1" />
                )}
                Sync Now
              </Button>
            </div>
          </CardTitle>
          {lastSync && (
            <CardDescription>
              Last synced: {formatDate(lastSync)}
            </CardDescription>
          )}
        </CardHeader>
        <CardContent>
          {/* Running Job Progress */}
          {runningJob && (
            <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                  {runningJob.current_step || 'Processing...'}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => cancelJob(runningJob.id)}
                  className="text-red-600 hover:text-red-700"
                >
                  Cancel
                </Button>
              </div>
              <Progress value={runningJob.progress} className="h-2" />
              <p className="text-xs text-gray-500 mt-1">{runningJob.progress}% complete</p>
            </div>
          )}

          {/* Stats Summary */}
          {syncStatus && (
            <div className="grid grid-cols-4 gap-3">
              <div className="text-center p-2 bg-gray-50 rounded">
                <p className="text-2xl font-bold">{syncStatus.total_items}</p>
                <p className="text-xs text-gray-500">Total Items</p>
              </div>
              <div className="text-center p-2 bg-green-50 rounded">
                <p className="text-2xl font-bold text-green-600">
                  {syncStatus.items_by_sync_status?.synced || 0}
                </p>
                <p className="text-xs text-gray-500">Synced</p>
              </div>
              <div className="text-center p-2 bg-yellow-50 rounded">
                <p className="text-2xl font-bold text-yellow-600">
                  {syncStatus.items_by_sync_status?.new || 0}
                </p>
                <p className="text-xs text-gray-500">New</p>
              </div>
              <div className="text-center p-2 bg-amber-50 rounded">
                <p className="text-2xl font-bold text-amber-600">
                  {syncStatus.items_by_sync_status?.stale || 0}
                </p>
                <p className="text-xs text-gray-500">Stale</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Sync Jobs */}
      {syncJobs.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Recent Sync Jobs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {syncJobs.slice(0, 3).map(job => {
                const StatusIcon = STATUS_CONFIG[job.status]?.icon || Clock;
                return (
                  <div 
                    key={job.id} 
                    className="flex items-center justify-between p-2 border rounded hover:bg-gray-50"
                    data-testid={`sync-job-${job.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <StatusIcon className={`w-4 h-4 ${
                        job.status === 'completed' ? 'text-green-600' :
                        job.status === 'failed' ? 'text-red-600' :
                        job.status === 'running' ? 'text-blue-600 animate-spin' :
                        'text-gray-400'
                      }`} />
                      <div>
                        <p className="text-sm font-medium flex items-center gap-2">
                          <Badge 
                            variant="outline" 
                            className={`text-xs ${STATUS_CONFIG[job.status]?.color} text-white`}
                          >
                            {STATUS_CONFIG[job.status]?.label || job.status}
                          </Badge>
                          <span className="text-gray-500 text-xs">
                            {job.source_type === 'file_upload' ? job.source_file : 'Manual sync'}
                          </span>
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatDate(job.created_at)}
                          {job.duration_seconds && ` • ${formatDuration(job.duration_seconds)}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {/* Stats */}
                      {job.status === 'completed' || job.status === 'partial' ? (
                        <div className="flex gap-3 text-xs">
                          {job.stats?.items_created > 0 && (
                            <span className="text-green-600 flex items-center gap-1">
                              <TrendingUp className="w-3 h-3" />
                              +{job.stats.items_created}
                            </span>
                          )}
                          {job.stats?.items_updated > 0 && (
                            <span className="text-blue-600 flex items-center gap-1">
                              <RefreshCw className="w-3 h-3" />
                              {job.stats.items_updated}
                            </span>
                          )}
                          {job.stats?.items_unchanged > 0 && (
                            <span className="text-gray-500 flex items-center gap-1">
                              <Minus className="w-3 h-3" />
                              {job.stats.items_unchanged}
                            </span>
                          )}
                          {job.stats?.items_failed > 0 && (
                            <span className="text-red-600 flex items-center gap-1">
                              <XCircle className="w-3 h-3" />
                              {job.stats.items_failed}
                            </span>
                          )}
                        </div>
                      ) : job.status === 'running' ? (
                        <span className="text-xs text-blue-600">{job.progress}%</span>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upload File Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent data-testid="upload-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Upload Vendor Catalog
            </DialogTitle>
            <DialogDescription>
              Upload a CSV file with vendor items to sync
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* File Input */}
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                uploadFile ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-gray-400'
              }`}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx"
                className="hidden"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                data-testid="file-input"
              />
              {uploadFile ? (
                <div className="flex items-center justify-center gap-2">
                  <FileSpreadsheet className="w-8 h-8 text-green-600" />
                  <div className="text-left">
                    <p className="font-medium">{uploadFile.name}</p>
                    <p className="text-sm text-gray-500">
                      {(uploadFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <Upload className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                  <p className="text-gray-600">Click to select a file</p>
                  <p className="text-xs text-gray-400">CSV or XLSX format</p>
                </>
              )}
            </div>

            {/* Expected Columns Info */}
            <div className="p-3 bg-gray-50 rounded text-sm">
              <p className="font-medium mb-1">Required columns:</p>
              <code className="text-xs">vendor_sku, name, cost_price</code>
              <p className="font-medium mt-2 mb-1">Optional columns:</p>
              <code className="text-xs">description, brand, msrp, category_code, product_type, image_urls</code>
            </div>

            {/* Options */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={uploadOptions.update_existing}
                  onChange={(e) => setUploadOptions(o => ({ ...o, update_existing: e.target.checked }))}
                  className="rounded"
                />
                Update existing items
              </Label>
              <Label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={uploadOptions.skip_unchanged}
                  onChange={(e) => setUploadOptions(o => ({ ...o, skip_unchanged: e.target.checked }))}
                  className="rounded"
                />
                Skip unchanged items
              </Label>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => setShowUploadModal(false)}>
                Cancel
              </Button>
              <Button 
                onClick={uploadCatalogFile} 
                disabled={!uploadFile || uploading}
                data-testid="confirm-upload-btn"
              >
                {uploading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                ) : (
                  <><Upload className="w-4 h-4 mr-2" /> Upload & Sync</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* History Modal */}
      <Dialog open={showHistoryModal} onOpenChange={setShowHistoryModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="history-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Sync History - {vendorName}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : syncJobs.length > 0 ? (
              syncJobs.map(job => {
                const StatusIcon = STATUS_CONFIG[job.status]?.icon || Clock;
                return (
                  <div 
                    key={job.id} 
                    className="border rounded-lg p-4"
                    data-testid={`history-job-${job.id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <StatusIcon className={`w-5 h-5 ${
                          job.status === 'completed' ? 'text-green-600' :
                          job.status === 'failed' ? 'text-red-600' :
                          job.status === 'running' ? 'text-blue-600 animate-spin' :
                          'text-gray-400'
                        }`} />
                        <Badge 
                          className={`${STATUS_CONFIG[job.status]?.color} text-white`}
                        >
                          {STATUS_CONFIG[job.status]?.label || job.status}
                        </Badge>
                        <span className="text-sm text-gray-500">
                          {job.source_type === 'file_upload' ? `File: ${job.source_file}` : 'Manual sync'}
                        </span>
                      </div>
                      {job.status !== 'pending' && job.status !== 'running' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteJob(job.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                      <div>
                        <span className="text-gray-500">Started:</span>
                        <span className="ml-2">{formatDate(job.started_at)}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Duration:</span>
                        <span className="ml-2">{formatDuration(job.duration_seconds)}</span>
                      </div>
                    </div>

                    {/* Stats */}
                    {job.stats && (
                      <div className="grid grid-cols-5 gap-2 text-xs mt-3 p-2 bg-gray-50 rounded">
                        <div className="text-center">
                          <p className="font-bold">{job.stats.total_items}</p>
                          <p className="text-gray-500">Total</p>
                        </div>
                        <div className="text-center">
                          <p className="font-bold text-green-600">+{job.stats.items_created}</p>
                          <p className="text-gray-500">Created</p>
                        </div>
                        <div className="text-center">
                          <p className="font-bold text-blue-600">{job.stats.items_updated}</p>
                          <p className="text-gray-500">Updated</p>
                        </div>
                        <div className="text-center">
                          <p className="font-bold text-gray-500">{job.stats.items_unchanged}</p>
                          <p className="text-gray-500">Unchanged</p>
                        </div>
                        <div className="text-center">
                          <p className="font-bold text-red-600">{job.stats.items_failed}</p>
                          <p className="text-gray-500">Failed</p>
                        </div>
                      </div>
                    )}

                    {/* Errors */}
                    {job.error_summary && (
                      <div className="mt-2 p-2 bg-red-50 rounded text-sm text-red-700">
                        {job.error_summary}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="text-center py-12 text-gray-500">
                <History className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No sync history</p>
                <p className="text-sm">Trigger a sync to see history here</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VendorSyncManager;
